import React, { useState } from "react";
import { HashRouter as Router, Routes, Route, NavLink } from "react-router-dom";

// ---------------------------------------------
// Main App Component
// ---------------------------------------------
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/cryptid-hunters" element={<CryptidHuntersPage />} />
        <Route path="/how-to-play" element={<HowToPlayPage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
    </Router>
  );
}

// ---------------------------------------------
// Layout Components
// ---------------------------------------------
function SiteLayout({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: "linear-gradient(180deg, #0a0e1a 0%, #1a0e1a 50%, #0a1a0e 100%)",
      color: "#ffffff",
      minHeight: "100vh"
    }}>
      <SiteHeader />
      <main>{children}</main>
      <GradientLine />
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  return (
    <header style={{
      padding: "20px 0"
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "0 60px"
      }}>
          <img 
            src="/neon-crow-logo-new.png" 
            alt="Neon Crow Games" 
            style={{
              height: "180px",
              width: "auto",
              filter: "drop-shadow(0 0 15px rgba(0, 255, 255, 0.6))",
              transition: "filter 0.3s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.filter = "drop-shadow(0 0 25px rgba(0, 255, 255, 0.9))"}
            onMouseLeave={(e) => e.currentTarget.style.filter = "drop-shadow(0 0 15px rgba(0, 255, 255, 0.6))"}
          />
        <nav style={{ display: "flex", gap: "40px", alignItems: "center" }}>
          <NavLink to="/" style={navLinkStyle}>HOME</NavLink>
          <NavLink to="/cryptid-hunters" style={navLinkStyle}>CRYPTID HUNTERS</NavLink>
          <NavLink to="/how-to-play" style={navLinkStyle}>HOW TO PLAY</NavLink>
          <NavLink to="/media" style={navLinkStyle}>MEDIA</NavLink>
          <NavLink to="/contact" style={navLinkStyle}>CONTACT</NavLink>
          <a 
            href="https://app.neoncrowgames.com/login" 
            style={{
              ...navLinkStyle,
              border: "2px solid #00ffff",
              padding: "12px 30px",
              borderRadius: "8px",
              background: "transparent",
              boxShadow: "0 0 15px rgba(0, 255, 255, 0.3)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(0, 255, 255, 0.1)";
              e.currentTarget.style.boxShadow = "0 0 25px rgba(0, 255, 255, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.boxShadow = "0 0 15px rgba(0, 255, 255, 0.3)";
            }}
          >
            LOG IN
          </a>
        </nav>
      </div>
    </header>
  );
}

function GradientLine() {
  return (
    <div style={{
      width: "100%",
      height: "3px",
      background: "linear-gradient(90deg, #ff0080 0%, #ff8000 25%, #ffff00 50%, #00ff88 75%, #00ffff 100%)",
      boxShadow: "0 0 20px rgba(0, 255, 255, 0.5)"
    }} />
  );
}

function SiteFooter() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await fetch("https://api.neoncrowgames.com/api/subscribers/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      setMessage(data.message);
      
      if (data.success) {
    setEmail("");
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      setMessage("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Newsletter Footer */}
      <footer style={{
        padding: "40px 0",
        borderTop: "1px solid rgba(0, 255, 255, 0.2)"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 60px"
        }}>
          <div>
          <div style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "28px",
            fontWeight: 600
          }}>
            Stay updated on the latest Neon Crow Games news!
            </div>
            {message && (
              <div style={{
                marginTop: "10px",
                fontSize: "16px",
                color: message.includes("error") || message.includes("occurred") ? "#ff6b6b" : "#00ffff",
                textShadow: message.includes("error") || message.includes("occurred") ? "0 0 10px rgba(255, 107, 107, 0.5)" : "0 0 10px rgba(0, 255, 255, 0.5)"
              }}>
                {message}
              </div>
            )}
          </div>
          <form onSubmit={handleSubmit} style={{ display: "flex", gap: "20px" }}>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              style={{
                fontFamily: "'Inter', sans-serif",
                padding: "15px 25px",
                border: "2px solid #00ffff",
                borderRadius: "8px",
                background: "transparent",
                color: "#ffffff",
                fontSize: "16px",
                width: "300px",
                boxShadow: "0 0 10px rgba(0, 255, 255, 0.2)"
              }}
              onFocus={(e) => e.currentTarget.style.boxShadow = "0 0 20px rgba(0, 255, 255, 0.4)"}
              onBlur={(e) => e.currentTarget.style.boxShadow = "0 0 10px rgba(0, 255, 255, 0.2)"}
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              style={{
                fontFamily: "'Orbitron', sans-serif",
                padding: "15px 40px",
                border: "2px solid #00ffff",
                borderRadius: "8px",
                background: "transparent",
                color: "#00ffff",
                fontSize: "16px",
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                boxShadow: "0 0 15px rgba(0, 255, 255, 0.3)",
                transition: "all 0.3s",
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                e.currentTarget.style.background = "rgba(0, 255, 255, 0.1)";
                e.currentTarget.style.boxShadow = "0 0 30px rgba(0, 255, 255, 0.6)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.boxShadow = "0 0 15px rgba(0, 255, 255, 0.3)";
              }}
            >
              {isSubmitting ? "SUBSCRIBING..." : "SUBSCRIBE"}
            </button>
          </form>
        </div>
      </footer>

      {/* Additional Footer */}
      <footer style={{
        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        marginTop: "0",
        padding: "40px 0"
      }}>
        <div style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 60px",
          fontSize: "14px",
          color: "#9ca3af"
        }}>
          <div style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "start",
            justifyContent: "space-between",
            gap: "24px",
            flexWrap: "wrap"
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <img 
                  src="/neon-crow-logo-new.png" 
                  alt="Neon Crow Games"
                  style={{
                    width: "32px",
                    height: "32px",
                    filter: "drop-shadow(0 0 6px rgba(6, 182, 212, 0.6))"
                  }}
                />
                <span style={{ fontWeight: "bold", color: "#ffffff" }}>Neon Crow Games</span>
              </div>
              <p style={{ fontSize: "12px", margin: "4px 0 0 0" }}>© {new Date().getFullYear()} Neon Crow Games. All rights reserved.</p>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: "0" }}>Strange worlds. Sharper minds.</p>
            </div>
            <div style={{ display: "flex", gap: "24px" }}>
              <a 
                href="#/privacy" 
                style={{ color: "#9ca3af", textDecoration: "none", transition: "color 0.3s" }}
                onMouseEnter={(e) => e.currentTarget.style.color = "#d1d5db"}
                onMouseLeave={(e) => e.currentTarget.style.color = "#9ca3af"}
              >
                Privacy
              </a>
              <a 
                href="mailto:neoncrowgames@gmail.com" 
                style={{ color: "#9ca3af", textDecoration: "none", transition: "color 0.3s" }}
                onMouseEnter={(e) => e.currentTarget.style.color = "#d1d5db"}
                onMouseLeave={(e) => e.currentTarget.style.color = "#9ca3af"}
              >
                neoncrowgames@gmail.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

// ---------------------------------------------
// Page Components
// ---------------------------------------------
function HomePage() {
  return (
    <SiteLayout>
      <div style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "60px 60px 40px"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start"
      }}>
        <div style={{ flex: 1, maxWidth: "600px" }}>
          <h1 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: "90px",
            fontWeight: 400,
            color: "#00ffff",
            lineHeight: 1,
            marginBottom: "30px",
            textShadow: "0 0 20px rgba(0, 255, 255, 0.6)",
            textTransform: "uppercase",
            letterSpacing: "2px"
          }}>
            CRYPTID<br />
            <span style={{ display: "block" }}>HUNTERS</span>
          </h1>
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "24px",
            lineHeight: 1.6,
            marginBottom: "40px",
            color: "#e0e0e0",
            fontWeight: 400
          }}>
            An upcoming card game of mysterious creatures and strategic capture.
          </p>
          <a 
            href="#/cryptid-hunters"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              display: "inline-block",
              padding: "18px 50px",
              border: "2px solid #00ffff",
              borderRadius: "8px",
              color: "#00ffff",
              textDecoration: "none",
              fontSize: "18px",
              fontWeight: 700,
              letterSpacing: "2px",
              textTransform: "uppercase",
              background: "transparent",
              boxShadow: "0 0 15px rgba(0, 255, 255, 0.3)",
              transition: "all 0.3s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(0, 255, 255, 0.1)";
              e.currentTarget.style.boxShadow = "0 0 30px rgba(0, 255, 255, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.boxShadow = "0 0 15px rgba(0, 255, 255, 0.3)";
            }}
          >
            LEARN MORE
          </a>
        </div>
        <div style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative"
        }}>
          <img 
            src="/cryptid-hunters-box.png" 
            alt="Cryptid Hunters Card Game Box and Cards" 
            style={{
              maxWidth: "600px",
              width: "100%",
              height: "auto",
              filter: "drop-shadow(0 10px 40px rgba(0, 255, 255, 0.3))",
              transition: "transform 0.3s ease, filter 0.3s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.02)";
              e.currentTarget.style.filter = "drop-shadow(0 15px 50px rgba(0, 255, 255, 0.5))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.filter = "drop-shadow(0 10px 40px rgba(0, 255, 255, 0.3))";
            }}
          />
        </div>
        </div>
      </div>
    </SiteLayout>
  );
}

function HowToPlayPage() {
  return (
    <SiteLayout>
      <div style={{ padding: "60px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={pageHeadingStyle}>HOW TO PLAY</h1>
        <p style={pageDescriptionStyle}>
          Learn the basics of Cryptid Hunters and start your journey into mysterious creature capture.
        </p>
        <div style={{ marginTop: "40px", color: "#e0e0e0", fontSize: "18px", lineHeight: 1.8 }}>
          <p>Game rules and instructions coming soon...</p>
        </div>
      </div>
    </SiteLayout>
  );
}

function CryptidHuntersPage() {
  return (
    <SiteLayout>
      <div style={{ padding: "60px", maxWidth: "1000px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "60px" }}>
          <h1 style={pageHeadingStyle}>CRYPTID HUNTERS</h1>
          <p style={pageDescriptionStyle}>
            A strategic card game of mysterious creatures and cunning capture
          </p>
        </div>

        {/* Coming Soon Banner */}
        <div style={{
          background: "linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, rgba(255, 128, 0, 0.1) 100%)",
          border: "2px solid #00ffff",
          borderRadius: "12px",
          padding: "50px 40px",
          textAlign: "center",
          marginBottom: "60px",
          boxShadow: "0 0 30px rgba(0, 255, 255, 0.3)"
        }}>
          <div style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: "56px",
            fontWeight: 700,
            color: "#00ffff",
            textShadow: "0 0 20px rgba(0, 255, 255, 0.8)",
            marginBottom: "15px",
            letterSpacing: "4px"
          }}>
            COMING SOON
          </div>
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "22px",
            color: "#e0e0e0",
            marginBottom: "35px"
          }}>
            The hunt begins in 2025
          </p>
          <a 
            href="https://app.neoncrowgames.com/register"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              display: "inline-block",
              padding: "18px 50px",
              border: "2px solid #00ffff",
              borderRadius: "8px",
              color: "#00ffff",
              textDecoration: "none",
              fontSize: "18px",
              fontWeight: 700,
              letterSpacing: "2px",
              textTransform: "uppercase",
              background: "transparent",
              boxShadow: "0 0 15px rgba(0, 255, 255, 0.3)",
              transition: "all 0.3s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(0, 255, 255, 0.1)";
              e.currentTarget.style.boxShadow = "0 0 30px rgba(0, 255, 255, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.boxShadow = "0 0 15px rgba(0, 255, 255, 0.3)";
            }}
          >
            Sign Up for Early Access
          </a>
        </div>

        {/* About */}
        <div style={{ 
          textAlign: "center",
          padding: "0 40px"
        }}>
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "20px",
            lineHeight: 1.7,
            color: "#e0e0e0",
            marginBottom: "0"
          }}>
            Track down legendary cryptids from Bigfoot to the Loch Ness Monster. 
            Compete against players worldwide in strategic multiplayer battles. 
            Build your deck and outwit your opponents to become the ultimate hunter.
          </p>
        </div>
      </div>
    </SiteLayout>
  );
}

function MediaPage() {
  return (
    <SiteLayout>
      <div style={{ padding: "60px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={pageHeadingStyle}>MEDIA</h1>
        <p style={pageDescriptionStyle}>
          Screenshots, videos, and artwork from Cryptid Hunters.
        </p>
        <div style={{ marginTop: "40px", color: "#e0e0e0", fontSize: "18px" }}>
          <p>Media gallery coming soon...</p>
        </div>
      </div>
    </SiteLayout>
  );
}

function ContactPage() {
  return (
    <SiteLayout>
      <div style={{ padding: "60px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={pageHeadingStyle}>CONTACT</h1>
        <p style={pageDescriptionStyle}>
          Get in touch with the Neon Crow Games team.
        </p>
        <div style={{ marginTop: "40px", color: "#e0e0e0", fontSize: "18px", lineHeight: 1.8 }}>
          <p>
            <strong style={{ color: "#00ffff" }}>Email:</strong>{" "}
            <a 
              href="mailto:neoncrowgames@gmail.com"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              neoncrowgames@gmail.com
            </a>
          </p>
          <p>
            <strong style={{ color: "#00ffff" }}>X (Twitter):</strong>{" "}
            <a 
              href="https://x.com/neon_crow_games"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              @neon_crow_games
            </a>
          </p>
          <p>
            <strong style={{ color: "#00ffff" }}>Discord:</strong>{" "}
            <a 
              href="https://discord.gg/Hs8eGcfp32"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              discord.gg/Hs8eGcfp32
            </a>
          </p>
          <p>
            <strong style={{ color: "#00ffff" }}>Facebook:</strong>{" "}
            <a 
              href="https://www.facebook.com/profile.php?id=61562906850303"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              Neon Crow Games
            </a>
          </p>
        </div>
      </div>
    </SiteLayout>
  );
}

function PrivacyPage() {
  const sectionStyle: React.CSSProperties = {
    marginTop: "40px",
    color: "#e0e0e0",
    fontSize: "16px",
    lineHeight: 1.8
  };

  const sectionHeadingStyle: React.CSSProperties = {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: "28px",
    color: "#00ffff",
    marginTop: "40px",
    marginBottom: "20px",
    textShadow: "0 0 15px rgba(0, 255, 255, 0.5)",
    letterSpacing: "1px"
  };

  return (
    <SiteLayout>
      <div style={{ padding: "60px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={pageHeadingStyle}>PRIVACY POLICY</h1>
        <p style={pageDescriptionStyle}>
          Last Updated: October 4, 2025
        </p>

        <div style={sectionStyle}>
          <p>
            At Neon Crow Games ("we," "us," or "our"), we are committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you visit our website and use our services, including Cryptid Hunters.
          </p>

          <h2 style={sectionHeadingStyle}>1. Information We Collect</h2>
          <p><strong>Personal Information</strong></p>
          <p>
            We may collect personal information that you voluntarily provide to us when you:
          </p>
          <ul style={{ marginLeft: "20px" }}>
            <li>Register for an account</li>
            <li>Subscribe to our newsletter</li>
            <li>Contact us via email</li>
            <li>Participate in our community features</li>
          </ul>
          <p>This information may include:</p>
          <ul style={{ marginLeft: "20px" }}>
            <li>Name and username</li>
            <li>Email address</li>
            <li>Password (securely encrypted)</li>
            <li>Game preferences and settings</li>
          </ul>

          <p style={{ marginTop: "20px" }}><strong>Automatically Collected Information</strong></p>
          <p>
            When you visit our website, we may automatically collect certain information about your device, 
            including information about your web browser, IP address, time zone, and some of the cookies 
            installed on your device.
          </p>

          <h2 style={sectionHeadingStyle}>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul style={{ marginLeft: "20px" }}>
            <li>Provide, operate, and maintain our services</li>
            <li>Create and manage your account</li>
            <li>Send you newsletters and marketing communications (if you've subscribed)</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Improve and optimize our website and games</li>
            <li>Detect and prevent fraud and abuse</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2 style={sectionHeadingStyle}>3. Data Storage and Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal 
            information against unauthorized access, alteration, disclosure, or destruction. Your passwords 
            are encrypted using industry-standard bcrypt hashing, and we use secure protocols for data 
            transmission.
          </p>
          <p>
            However, please note that no method of transmission over the internet or electronic storage is 
            100% secure, and we cannot guarantee absolute security.
          </p>

          <h2 style={sectionHeadingStyle}>4. Cookies and Tracking Technologies</h2>
          <p>
            We may use cookies and similar tracking technologies to track activity on our website and hold 
            certain information. Cookies are files with a small amount of data that may include an anonymous 
            unique identifier. You can instruct your browser to refuse all cookies or to indicate when a 
            cookie is being sent.
          </p>

          <h2 style={sectionHeadingStyle}>5. Third-Party Services</h2>
          <p>
            We may employ third-party companies and individuals to facilitate our service, provide the 
            service on our behalf, perform service-related services, or assist us in analyzing how our 
            service is used. These third parties have access to your personal information only to perform 
            these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
          </p>

          <h2 style={sectionHeadingStyle}>6. Email Communications</h2>
          <p>
            If you subscribe to our newsletter, we will use your email address to send you updates about 
            Neon Crow Games and our products. You may unsubscribe from these communications at any time by 
            clicking the "unsubscribe" link in any email we send, or by contacting us directly at 
            neoncrowgames@gmail.com.
          </p>

          <h2 style={sectionHeadingStyle}>7. Children's Privacy</h2>
          <p>
            Our services are not intended for children under the age of 13. We do not knowingly collect 
            personal information from children under 13. If you are a parent or guardian and you are aware 
            that your child has provided us with personal information, please contact us so that we can take 
            necessary action.
          </p>

          <h2 style={sectionHeadingStyle}>8. Your Data Rights</h2>
          <p>Depending on your location, you may have the following rights regarding your personal data:</p>
          <ul style={{ marginLeft: "20px" }}>
            <li><strong>Access:</strong> Request access to your personal data</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a structured format</li>
            <li><strong>Objection:</strong> Object to our processing of your personal data</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent where we rely on consent to process your data</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at neoncrowgames@gmail.com.
          </p>

          <h2 style={sectionHeadingStyle}>9. Data Retention</h2>
          <p>
            We will retain your personal information only for as long as necessary to fulfill the purposes 
            outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. 
            When we no longer need your information, we will securely delete or anonymize it.
          </p>

          <h2 style={sectionHeadingStyle}>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and maintained on computers located outside of your state, 
            province, country, or other governmental jurisdiction where data protection laws may differ. By 
            using our services, you consent to the transfer of your information to these locations.
          </p>

          <h2 style={sectionHeadingStyle}>11. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
            the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to 
            review this Privacy Policy periodically for any changes.
          </p>

          <h2 style={sectionHeadingStyle}>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p style={{ marginTop: "10px" }}>
            <strong>Neon Crow Games</strong><br />
            Email: <a 
              href="mailto:neoncrowgames@gmail.com"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              neoncrowgames@gmail.com
            </a><br />
            X (Twitter): <a 
              href="https://x.com/neon_crow_games"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              @neon_crow_games
            </a><br />
            Discord: <a 
              href="https://discord.gg/Hs8eGcfp32"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              discord.gg/Hs8eGcfp32
            </a><br />
            Facebook: <a 
              href="https://www.facebook.com/profile.php?id=61562906850303"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e0e0e0", textDecoration: "none", transition: "color 0.3s" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#00ffff"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#e0e0e0"}
            >
              Neon Crow Games
            </a>
          </p>

          <div style={{
            marginTop: "60px",
            padding: "30px",
            border: "1px solid rgba(0, 255, 255, 0.2)",
            borderRadius: "8px",
            background: "rgba(0, 255, 255, 0.03)"
          }}>
            <p style={{ fontSize: "14px", color: "#9ca3af", margin: 0 }}>
              This privacy policy is designed to comply with general data protection principles. 
              Depending on your jurisdiction, additional rights and protections may apply. We are 
              committed to transparency and protecting your privacy in accordance with applicable laws.
            </p>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}

// ---------------------------------------------
// Styles
// ---------------------------------------------
const navLinkStyle: React.CSSProperties = {
  fontFamily: "'Orbitron', sans-serif",
  color: "#00ffff",
  textDecoration: "none",
  fontSize: "16px",
  fontWeight: 700,
  letterSpacing: "2px",
  textTransform: "uppercase",
  transition: "all 0.3s",
  textShadow: "0 0 10px rgba(0, 255, 255, 0.5)"
};

const pageHeadingStyle: React.CSSProperties = {
  fontFamily: "'Orbitron', sans-serif",
  fontSize: "60px",
  fontWeight: 700,
  color: "#00ffff",
  textShadow: "0 0 20px rgba(0, 255, 255, 0.6)",
  textTransform: "uppercase",
  letterSpacing: "2px",
  marginBottom: "20px"
};

const pageDescriptionStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: "24px",
  color: "#e0e0e0",
  lineHeight: 1.6
};

export default App;
